/**
 * stylePackToStyle — стиль-пак → CSS-переменные.
 *
 * Разделение ответственности:
 *   • пак задаёт ФОРМУ (радиусы, плотность, типографскую шкалу, движение);
 *   • палитра/тема задаёт ЦВЕТ (themeToStyle);
 *   • ручные настройки пользователя перекрывают и то, и другое.
 *
 * Поэтому здесь НЕТ ни одного цвета — guard-тест это стережёт.
 *
 * Переменные читает PublicBlockRenderer(Extended), общий для квиза и пейволла,
 * поэтому пак действует на обе поверхности одинаково.
 */
import type React from 'react';
// WEB-3447, заход 2: дополнительные переменные пака — через дверь ленивого
// куска (`styleKitDoor.ts`); без куска — только десктопные двойники.
import { h1SizeCss, stylePackExtraVars } from './styleKitDoor';
import { ctaDisabledVars } from './ctaDisabledStyle';
import type {
  ScreenAlignX,
  ScreenAlignY,
  ScreenOverlayTone,
  SemanticAccentSource,
  StylePack,
} from './stylePack.types';

/**
 * Оси пака резолвятся в flex-значения: сырые 'top'/'left' CSS применить не
 * может — это тот же класс мёртвого поля, что был у shape.shadow (--shadow-level).
 * Вертикаль подставляется в justify-content колонки, горизонталь — в align-items.
 */
function flexAlignY(align: ScreenAlignY): string {
  return align === 'top' ? 'flex-start' : align === 'bottom' ? 'flex-end' : 'center';
}

function flexAlignX(align: ScreenAlignX): string {
  return align === 'left' ? 'flex-start' : align === 'right' ? 'flex-end' : 'center';
}

/**
 * Оверлей фона отдаётся ДВУМЯ готовыми к градиенту значениями — направлением и
 * прозрачностью, — а не строкой градиента: сам цвет вуали пак задавать не имеет
 * права (guard-тест «пак не эмитит цветов»), чёрным её красит рендерер.
 * Прозрачности совпадают с scrimGradient в ScreenBackgroundWrapper (0.70 / 0.55),
 * чтобы дефолт пака не разъехался с сегодняшним видом карточного фона.
 */
function overlayDirection(tone: ScreenOverlayTone): string {
  // scrim-bottom растёт снизу вверх, ровная вуаль — сверху вниз.
  return tone === 'scrim-full' ? 'to bottom' : 'to top';
}

function overlayOpacity(tone: ScreenOverlayTone): string {
  return tone === 'scrim-bottom' ? '0.7' : tone === 'scrim-full' ? '0.55' : '0';
}

/**
 * WEB-925: семантический акцент отдаётся ССЫЛКОЙ на переменную палитры, а не
 * цветом — пак не имеет права владеть цветом (guard-тест). Приём тот же, что у
 * `--option-border-color`: пак решает РОЛЬ, палитра решает оттенок, поэтому
 * «деньги» остаются зелёными в зелёной теме и мятными в мятной.
 *
 * WEB-1016: ссылка идёт на КОНТРАСТНЫЙ вариант источника (`*-ink`), а не на
 * сырую переменную. Сырой `--color-accent` у палитры помечен «reserved» и
 * бывает светлым тинтом — как заливка чипа «Most Popular» с белым текстом он
 * давал белое по белому (замер 1.02–1.31:1 при норме 3:1). `*-ink` — тот же
 * цвет палитры, доведённый до различимости (см. themes/semanticAccent.ts).
 * Фолбэк на сырую переменную оставлен: если стиль собран веткой, которая
 * `*-ink` ещё не эмитит, элемент получит сегодняшний цвет, а не пустоту.
 *
 * `none` → currentColor: роль не выделяется, элемент наследует цвет текста;
 * это не цвет пака, а отказ от собственного.
 */
function accentVar(source: SemanticAccentSource): string {
  switch (source) {
    case 'success':
      return 'var(--color-success-ink, var(--color-success))';
    case 'accent':
      return 'var(--color-accent-ink, var(--color-accent, var(--color-primary)))';
    case 'brand':
      return 'var(--color-brand-ink, var(--color-primary))';
    default:
      return 'currentColor';
  }
}

/**
 * WEB-1016: цвет ТЕКСТА поверх заливки акцентом. Без него двухцветность
 * читается только с одной стороны: чип тарифа заливается акцентом, а подпись
 * на нём брала `--color-on-primary` — цвет, посчитанный для БРЕНДОВОЙ кнопки,
 * а не для этой заливки. На паках, где премиум ≠ бренд, это и есть «текст
 * пропал». `none` заливки не даёт — там остаётся сегодняшний цвет.
 */
function accentOnVar(source: SemanticAccentSource): string {
  switch (source) {
    case 'success':
      return 'var(--color-on-success-ink, var(--color-on-primary))';
    case 'accent':
      return 'var(--color-on-accent-ink, var(--color-on-primary))';
    case 'brand':
      return 'var(--color-on-brand-ink, var(--color-on-primary))';
    default:
      return 'var(--color-on-primary)';
  }
}

/** Радиус pill считается от высоты кнопки: половина высоты = идеальная пилюля. */
function buttonRadius(pack: StylePack): number {
  return pack.shape.radiusButton === 'pill'
    ? Math.round(pack.cta.height / 2)
    : pack.shape.radiusButton;
}

export function stylePackToStyle(pack: StylePack): React.CSSProperties {
  const { shape, density, type, states, layout } = pack;
  const isRing = shape.selectionMode === 'ring';

  const style: Record<string, string> = {
    // ── форма ────────────────────────────────────────────────────────────
    // radiusCard пишем в --border-radius-lg: рендерер блоков уже читает эту
    // переменную (атом 1), так что карточки подхватывают пак без правок.
    '--border-radius-lg': `${shape.radiusCard}px`,
    '--border-radius-md': `${Math.max(0, Math.round(shape.radiusCard * 0.6))}px`,
    '--border-radius-sm': `${Math.max(0, Math.round(shape.radiusCard * 0.3))}px`,
    '--border-radius-button': `${buttonRadius(pack)}px`,
    '--radius-card': `${shape.radiusCard}px`,
    '--radius-option': `${shape.radiusOption}px`,
    '--radius-image': `${shape.radiusImage}px`,
    // В режиме ring рамка обнуляется, а её роль играет кольцо box-shadow:
    // оно не занимает места и не двигает вёрстку при выборе (приём
    // EveryDoggy/Astroline). Цвет — ТОЛЬКО ссылка на переменную темы, без
    // собственного фолбэка: пак не имеет права задавать цвет (guard-тест). Тему
    // и палитру эмитят --color-border-primary всегда, когда оформление задано;
    // без оформления кольцо просто не рисуется — это не хуже прежнего вида.
    '--option-border-width': isRing ? '0px' : `${shape.borderWidth}px`,
    '--option-ring': isRing
      ? `0 0 0 ${shape.borderWidth}px var(--color-border-primary)`
      : 'none',
    // Как выглядит ВЫБРАННЫЙ вариант. Пак решает режим, но цвета берутся
    // ссылками на палитру — собственных цветов пак не задаёт.
    '--option-selected-border': 'var(--color-primary)',
    '--option-selected-bg':
      states.selected === 'filled'
        ? 'var(--color-primary)'
        : states.selected === 'gradient'
          ? // Решение владельца №2: градиент бренда из темы/палитры; не задан —
            // та же заливка, что у 'filled'. Только ссылки — пак цветов не задаёт.
            'var(--gradient-selected, var(--color-primary))'
          : states.selected === 'brand-border-tint'
            ? 'var(--color-bg-surface)'
            : 'transparent',
    // WEB-922: тон рамки резолвится в ССЫЛКУ на палитру (сырой enum был мёртв).
    // brand-soft — бледный бренд через color-mix (12min #DDDEFA-класс), нейтрали
    // отдаются границе темы (тёплую/холодную пару даёт сама палитра), strong —
    // вторичный текст, none — прозрачная. Собственных цветов пак не эмитит.
    '--option-border-color':
      shape.borderTone === 'brand-soft'
        ? 'color-mix(in srgb, var(--color-primary) 25%, transparent)'
        : shape.borderTone === 'strong'
          ? 'var(--color-text-secondary)'
          : shape.borderTone === 'none'
            ? 'transparent'
            : 'var(--color-border-primary)',
    '--selection-mode': shape.selectionMode,
    // Тень отдаётся ГОТОВЫМ CSS-значением: сырой enum ('soft') CSS применить
    // не может — до аудита 2026-07-31 поле shape.shadow было мёртвым. Значения
    // сняты с референсов: soft — карточки pastel-serif, lifted — выбранный
    // тариф пейволла 12min. Читает cardStyle рендерера (var(--card-shadow)).
    '--card-shadow':
      shape.shadow === 'soft'
        ? '0 2px 8px rgba(0, 0, 0, 0.06)'
        : shape.shadow === 'lifted'
          ? '0 10px 15px -3px rgba(0, 0, 0, 0.10)'
          : 'none',

    // ── плотность ────────────────────────────────────────────────────────
    '--option-h': `${density.optionHeight}px`,
    '--option-pad-y': `${density.optionPadY}px`,
    '--option-pad-x': `${density.optionPadX}px`,
    '--option-gap': `${density.gap}px`,
    '--content-max-w': `${density.contentMaxWidth}px`,
    '--screen-pad-x': `${density.screenPadX}px`,

    // ── раскладка экрана (WEB-927) ───────────────────────────────────────
    // Дефолты пака для того, что PM ратифицировал как свойство экрана: где
    // стоит контент и чем гасится фоновая картинка под ним. Экран может
    // переопределить обе оси (EditorScreen.layout) и тон (background.overlay);
    // рендерер этих переменных появится следующим атомом.
    '--screen-align-y': flexAlignY(layout.alignY),
    '--screen-align-x': flexAlignX(layout.alignX),
    '--screen-overlay-direction': overlayDirection(layout.backgroundOverlay),
    '--screen-overlay-opacity': overlayOpacity(layout.backgroundOverlay),

    // ── типографика ──────────────────────────────────────────────────────
    // WEB-1006: кегль пака — ПОТОЛОК, а не фикс. Фиксированные px не ужимались
    // на мобильной ширине (36px на 320 — заголовок в пять строк, скрин PM), а
    // отзывчивый фолбэк рендерера пак затирал. База ширины — var(--vp-w):
    // на пабликах честный вьюпорт (100vw), в превью — рамка телефона (320px),
    // поэтому превью и телефон считают ОДИНАКОВО (класс «vw врёт в превью»).
    // Пол не выше потолка: min() снаружи держит малые кегли паков как есть.
    '--h1-size': h1SizeCss(type.h1Size),
    '--h1-weight': String(type.h1Weight),
    '--h1-tracking': `${type.h1Tracking}px`,
    '--q-size': `min(${type.qSize}px, max(19px, calc(var(--vp-w, 100vw) * 0.06)))`,
    '--q-weight': String(type.qWeight),
    '--q-tracking': `${type.qTracking}px`,
    '--option-size': `${type.optionSize}px`,
    '--option-weight': String(type.optionWeight),
    '--heading-align': type.headingAlign,
    // Текст варианта по центру строки (пак outline-pills, WEB-3718); нет поля —
    // переменных нет, строка и подпись у значка выравниваются как раньше.
    ...(type.optionAlign === 'center'
      ? {
          '--option-justify': 'center',
          '--option-text-align': 'center',
          '--option-icon-text-align': 'start',
        }
      : {}),
    '--overline-transform': type.overlineTransform,

    // ── движение ─────────────────────────────────────────────────────────
    '--transition-ms': `${states.transitionMs}ms`,
    '--screen-fade-ms': `${states.screenFadeMs}ms`,
    // WEB-922: hover-тинт опций — лёгкая брендовая подложка через color-mix
    // (ссылка на палитру, не собственный цвет). Читает .funnel-option:hover
    // в public-paywall.css; inline-фон ВЫБРАННОЙ опции сильнее класса, поэтому
    // hover не перебивает выбранное состояние.
    '--option-hover-bg': states.hoverTint
      ? 'color-mix(in srgb, var(--color-primary) 6%, transparent)'
      : 'transparent',
    // WEB-922 (решение PM 2026-07-31): cta.disabledStyle. Настоящего
    // disabled у Next нет (валидация — warning по клику, WEB-895) — пока на
    // экране есть незаполненные required, кнопка несёт класс
    // .funnel-next-unfilled (визарды), а пак решает, КАК её приглушать:
    // muted-brand — только прозрачность (бренд остаётся, 12min),
    // grey — прозрачность + обесцвечивание.
    // 'muted-brand-dark' (эталон Slimkit) — 0.5; затемнение 15 % кладёт
    // ctaPackButtonStyle (components/quiz-runtime/ctaDockStyle.ts) внутренней тенью.
    // Значения — одна дверь с полем воронки settings.quizCta (ctaDisabledStyle.ts).
    ...ctaDisabledVars(pack.cta.disabledStyle),

    // ── пейволл (WEB-925) ────────────────────────────────────────────────
    // Эмитится ТОЛЬКО то, что CSS способен применить. Формат таймера, вид
    // промо-тикета, режим цены и способ выделения тарифа — решения о РАЗМЕТКЕ:
    // их читает рендерер прямо из пака. Сырой enum в CSS-переменной = мёртвое
    // поле, этот шрам уже был у shape.shadow (--shadow-level).
    '--accent-money': accentVar(pack.accents.money),
    '--accent-premium': accentVar(pack.accents.premium),
    // WEB-1016: парный цвет текста на заливке премиальной роли (чип
    // рекомендованного тарифа — единственное место, где акцент идёт ЗАЛИВКОЙ
    // под текстом). Денежная роль такой пары НЕ получает: она красит рамки,
    // обводки и сам текст, заливки под текстом у неё нет, а объявленная и
    // никем не читаемая переменная — мёртвое поле (шрам --shadow-level,
    // гард «каждая новая переменная читается разметкой»).
    '--accent-premium-on': accentOnVar(pack.accents.premium),
    // Приглушение невыбранных тарифов — готовое число, а не флаг:
    // CSS-переменную можно подставить прямо в opacity.
    //
    // WEB-1015 п.2: замеренные у Astroline ~0.5 брать нельзя. Непрозрачность
    // вешается на ВСЮ карточку, поэтому её текст композитится с фоном страницы
    // и теряет контраст к собственной плашке. Посчитано по всем живым темам
    // (плашка = colors.surface, текст = pickOnPrimaryColor от неё, фон =
    // colors.background и белый — обёртка фоновой картинки пиннится белой,
    // WEB-872):
    //   0.5 → 3.06:1 в худшем случае (bento-brutalist) — ниже AA 4.5:1;
    //   0.6 → 4.15:1 — всё ещё ниже;
    //   0.7 → 5.73:1 — AA с запасом, приглушение при этом хорошо заметно.
    // Читаемость приглушённого тарифа важнее точного повторения референса:
    // нечитаемый текст на пейволле у нас уже был (WEB-966, контраст 1:1).
    // Гард с пересчётом контраста — PublicBlockRendererExtended.planHighlight.web1015.
    '--plan-unselected-opacity': pack.paywall.dimUnselectedPlans ? '0.7' : '1',

    // ── решения владельца №4–5 (2026-10-08, повтор эталона Slimkit) ──────────────
    // Появление экрана, поля на телефоне, интерлиньяж/трекинг и десктопная
    // типографика — отдельным модулем: у старых паков он не отдаёт ни одного
    // нового ключа, кроме десктопных двойников (см. stylePackExtraVars.ts).
    ...stylePackExtraVars(pack),
  };

  return style as React.CSSProperties;
}
